import type { PortalRuntimeContext } from './runtimeContext';

import type { Lot } from '../types';
import { ensureAccessToken } from '../modules/api';
import { ensureSettings, save, VIEW_MODES, persistViewModePreference } from '../modules/state';
import { els, showToast, openCheckoutModal } from '../modules/ui';
import {
  handleCreditAdjustmentSubmit,
  handleSubscriptionExtensionSubmit,
  fetchAndPopulateUsers,
  renderUserCreditInfo,
} from '../modules/superadmin';
import {
  handleRemoveClosedLots,
  handleRemoveDeadMeat,
  handleArchiveUrls,
} from '../modules/bulkOperations';
import { getEffectiveCloseTime } from '../modules/storage';
import {
  resetRemoteSnapshot,
  applyRemoteSnapshot,
  mapRemoteDocument,
} from '../modules/lotOperations';
import { applyLotFilters } from '../modules/lotOperations/filtering';
import { sortLots } from '../modules/lotOperations/sorting';
import { exportVisibleLots } from '../modules/csvExport';
import { handleBulkPriceCheck } from '../modules/bulkPriceCheck';
import { cancelPolling } from '../modules/polling';
import { setupEventHandlers } from '../globalEventHandlers';
import {
  createEstimateModalHandler,
  createCloseTimeCommitHandler,
  createCloseTimeClearHandler,
  prepareLotFormForCreate,
} from '../modalController';
import {
  setupPaginationHandlers,
  setupFilterHandlers,
  setupSortHandlers,
  setupDashboardHandlers,
  setupSettingsHandlers,
  setupLotFormHandlers,
} from '../eventHandlers';
import { getUserProfile, upsertUserProfile } from '../modules/auth';
import { __archiveUrl } from '../modules/image';

export function registerStaticHandlers(ctx: PortalRuntimeContext): void {
  const {
    cfg,
    state,
    render,
    DEBUG,
    startRemoteLotsFetch,
    pendingViewModeRefreshRef,
    applyModeBannerForConfig,
    lastProfileSaveStatusRef,
  } = ctx;

  const modalDeps = {
    state,
    render,
    save,
    ensureAccessToken,
    applyModeBanner: applyModeBannerForConfig,
    DEBUG,
  };

  const handleCommitEstimateEditFromModal = createEstimateModalHandler(modalDeps);
  const commitCloseTimeEdit = createCloseTimeCommitHandler(modalDeps);
  const clearCloseTimeEdit = createCloseTimeClearHandler(modalDeps);

  setupEventHandlers({
    state,
    els,
    render,
    ensureAccessToken,
    cfg,
    DEBUG,
    handleCreditAdjustmentSubmit,
    handleSubscriptionExtensionSubmit,
    commitEstimateEditFromModal: handleCommitEstimateEditFromModal,
    clearCloseTimeEdit,
    commitCloseTimeEdit,
    prepareLotFormForCreate,
  });

  if (els.removeClosedLots) {
    els.removeClosedLots.addEventListener('click', e => {
      handleRemoveClosedLots(
        e,
        state,
        (lot: Lot) => {
          const value = getEffectiveCloseTime(lot);
          return typeof value === 'number' ? value : 0;
        },
        render,
        save,
        applyRemoteSnapshot,
        ensureAccessToken,
        (window as any).__LOT_DEBUG === true
      );
    });
  }

  if (els.removeDeadMeat) {
    els.removeDeadMeat.addEventListener('click', e =>
      handleRemoveDeadMeat(
        e,
        state,
        ensureSettings,
        render,
        save,
        applyRemoteSnapshot,
        ensureAccessToken,
        (window as any).__LOT_DEBUG === true
      )
    );
  }

  if (els.archiveUrls) {
    els.archiveUrls.addEventListener('click', () => handleArchiveUrls(state, render, __archiveUrl));
  }

  if (els.exportCsv) {
    els.exportCsv.addEventListener('click', () => {
      try {
        // Export everything matching the current search/filters (all pages).
        const filtered = sortLots(applyLotFilters(state.lots || []));
        if (!filtered.length) {
          showToast('No lots to export.', { variant: 'warning', duration: 2500 });
          return;
        }
        const count = exportVisibleLots(state, filtered, (lot: Lot) => {
          const value = getEffectiveCloseTime(lot);
          return typeof value === 'number' ? value : null;
        });
        showToast(`Exported ${count} lot${count === 1 ? '' : 's'} to CSV.`, {
          variant: 'success',
          duration: 2500,
        });
      } catch (err) {
        showToast('CSV export failed: ' + (err instanceof Error ? err.message : String(err)), {
          variant: 'error',
          duration: 4000,
        });
      }
    });
  }

  if (els.groupByAuction) {
    const reflectGrouping = () => {
      const active = !!ensureSettings().groupByAuction;
      els.groupByAuction!.setAttribute('data-active', String(active));
      els.groupByAuction!.setAttribute('aria-pressed', String(active));
    };
    reflectGrouping();
    els.groupByAuction.addEventListener('click', () => {
      const settings = ensureSettings();
      settings.groupByAuction = !settings.groupByAuction;
      save();
      reflectGrouping();
      render();
    });
  }

  if (els.bulkPriceCheck) {
    els.bulkPriceCheck.addEventListener('click', () => {
      void handleBulkPriceCheck({
        state,
        render,
        ensureAccessToken,
        mapRemoteDocument,
        applyRemoteSnapshot,
        openCheckoutModal,
      });
    });
  }

  setupPaginationHandlers(
    {
      pageSize: els.pageSize as HTMLSelectElement,
      prev: els.prev as HTMLButtonElement,
      next: els.next as HTMLButtonElement,
      q: els.q as HTMLInputElement,
    },
    state,
    render
  );

  setupFilterHandlers(
    {
      filterFavorite: els.filterFavorite,
      filterClosingToday: els.filterClosingToday,
      filterClosingWeek: els.filterClosingWeek,
      filterHighMargin: els.filterHighMargin,
      filterNeedsPrice: els.filterNeedsPrice,
      filterActiveBids: els.filterActiveBids,
    },
    { state, render, showToast }
  );

  setupSortHandlers({ sortClose: els.sortClose }, state, render);

  setupDashboardHandlers(
    {
      openDashboard: els.openDashboard,
      closeDashboardModal: els.closeDashboardModal,
      dashboardModalBackdrop: els.dashboardModalBackdrop,
      dashboardModalContent: els.dashboardModalContent,
    },
    state
  );

  setupSettingsHandlers({
    state,
    els: {
      openOptions: els.openOptions,
      saveSettings: els.saveSettings,
      cancelSettings: els.cancelSettings,
      firstRunSkip: els.firstRunSkip,
      firstRunSubmit: els.firstRunSubmit,
      firstRunCompany: els.firstRunCompany,
      firstRunTimeZone: els.firstRunTimeZone,
      firstRunZip: els.firstRunZip,
      settingsZip: els.settingsZip,
      settingsTz: els.settingsTz,
      settingsDistanceUnits: els.settingsDistanceUnits,
      settingsUnitsAutoIndicator: els.settingsUnitsAutoIndicator,
      settingsTaxRate: els.settingsTaxRate,
      settingsBidderPremium: els.settingsBidderPremium,
      settingsSellerCosts: els.settingsSellerCosts,
      settingsFixedLotCost: els.settingsFixedLotCost,
      settingsEnableNotifications: els.settingsEnableNotifications,
      settingsNotifyLeadMinutes: els.settingsNotifyLeadMinutes,
      settingsNotificationsPermissionStatus: els.settingsNotificationsPermissionStatus,
      profileCompany: els.profileCompany,
      optionsCard: els.optionsCard,
      mobileMenuClose: els.mobileMenuClose,
      mobileMenuBackdrop: els.mobileMenuBackdrop,
      mobileViewAllLots: els.mobileViewAllLots,
      mobileOpenOptions: els.mobileOpenOptions,
      mobileLogout: els.mobileLogout,
      viewModeToggle: els.viewModeToggle,
      logout: els.logout,
    },
    render,
    showToast,
    cfg,
    getUserProfile,
    upsertUserProfile,
    lastProfileSaveStatus: lastProfileSaveStatusRef,
  });

  setupLotFormHandlers({
    state,
    els: {
      form: els.form,
      f: els.f,
    },
    render,
    applyModeBanner: applyModeBannerForConfig,
    DEBUG: cfg?.debug || false,
  });

  const clearBtn = document.getElementById('clear-form');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      prepareLotFormForCreate();
      if (els.f && els.f.title) {
        els.f.title.focus();
      }
    });
  }

  if (els.viewModeToggle) {
    els.viewModeToggle.addEventListener('click', () => {
      if (!(state.user && state.user.isSuperUser)) {
        return;
      }
      const nextMode =
        state.viewMode === VIEW_MODES.SUPERADMIN ? VIEW_MODES.USER : VIEW_MODES.SUPERADMIN;
      if (state.viewMode === nextMode) {
        return;
      }
      state.viewMode = nextMode;
      state.page = 1;
      state.impersonatedUserId = null;
      persistViewModePreference(state);
      if (state.viewMode === VIEW_MODES.SUPERADMIN && cfg) {
        void fetchAndPopulateUsers(cfg);
      }
      resetRemoteSnapshot();
      state.remoteError = '';
      state.lots = [];
      cancelPolling();
      render();
      applyModeBannerForConfig();
      if (state.mode === 'auth0') {
        if (state.isFetchingRemote) {
          pendingViewModeRefreshRef.value = true;
        } else {
          startRemoteLotsFetch();
        }
      }
    });
  }

  if (els.superadminUserSelect) {
    els.superadminUserSelect.addEventListener('change', async () => {
      if (!(state.user && state.user.isSuperUser)) {
        console.warn('[superadmin-user-select] User is not a superuser');
        return;
      }
      if (state.viewMode !== VIEW_MODES.SUPERADMIN) {
        console.warn('[superadmin-user-select] Not in superadmin view mode:', state.viewMode);
        return;
      }
      const selectedUserId = (els.superadminUserSelect as HTMLSelectElement).value;
      state.impersonatedUserId = selectedUserId || null;
      console.log(
        '[superadmin-user-select] Selected user ID:',
        selectedUserId,
        'Impersonated:',
        state.impersonatedUserId
      );
      renderUserCreditInfo();
      state.page = 1;
      resetRemoteSnapshot();
      state.remoteError = '';
      state.lots = [];
      cancelPolling();
      render();
      if (state.mode === 'auth0') {
        if (state.isFetchingRemote) {
          logger.debug('[superadmin-user-select] Fetch in progress, setting pending flag');
          pendingViewModeRefreshRef.value = true;
        } else {
          console.log(
            '[superadmin-user-select] Starting remote lots fetch for user:',
            state.impersonatedUserId
          );
          startRemoteLotsFetch();
        }
      }
    });
  }
}
